import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import ApiService from '../../services/Api.service';
import { formatMusicalKey } from '../../utils/formatMusicalKey';
import CamelotKeyChip from '../tracks/CamelotKeyChip';
import {
  fetchLibraryTracksByIds,
  isCatalogTrackId,
  isLocalTrackId,
  librarySummaryToTrack,
} from './AgentChatTrackHydrate';
import { ParsedAttachedTrackMessage } from './agentAttachedTrack';

type TrackView = ParsedAttachedTrackMessage['track'] & {
  albumCover?: string;
};

const resolveKey = (
  ...values: Array<string | number | undefined>
): string | undefined => {
  for (const value of values) {
    const key = formatMusicalKey(value);
    if (key) return key;
  }
  return undefined;
};

interface AgentAttachedTrackCardProps {
  track: ParsedAttachedTrackMessage['track'];
  /** When set, shows a dismiss control (composer attachment). */
  onRemove?: () => void;
}

/**
 * Seed track card for user messages and the composer attachment preview.
 */
export const AgentAttachedTrackCard: React.FC<AgentAttachedTrackCardProps> = ({
  track,
  onRemove,
}) => {
  const [view, setView] = useState<TrackView>(track);

  useEffect(() => {
    setView(track);
    let cancelled = false;

    void (async () => {
      if (isCatalogTrackId(track.id)) {
        const hydrated = await ApiService.getTrackById(track.id);
        if (cancelled || !hydrated?.ID) return;
        setView((current) => ({
          ...current,
          artist: hydrated.Artist || current.artist,
          title: hydrated.Title || current.title,
          albumCover: hydrated.AlbumCover || current.albumCover,
          bpm: hydrated.BPM ?? current.bpm,
          key: resolveKey(hydrated.Key_Camelot, hydrated.Key, current.key),
        }));
        return;
      }

      if (!isLocalTrackId(track.id)) return;
      const [summary] = await fetchLibraryTracksByIds([Number(track.id)]);
      if (cancelled || !summary) return;
      const hydrated = librarySummaryToTrack(summary);
      setView((current) => ({
        ...current,
        artist: hydrated.Artist || current.artist,
        title: hydrated.Title || current.title,
        albumCover: hydrated.AlbumCover || current.albumCover,
        bpm: hydrated.BPM ?? current.bpm,
        key: resolveKey(hydrated.Key_Camelot, hydrated.Key, current.key),
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [track]);

  const bpm =
    typeof view.bpm === 'number' && Number.isFinite(view.bpm)
      ? Math.round(view.bpm)
      : null;

  return (
    <div className="agent-user-track-card">
      {view.albumCover ? (
        <img src={view.albumCover} alt="" />
      ) : (
        <span className="agent-track-art-fallback" />
      )}
      <span className="agent-track-copy">
        <span className="agent-track-title">{view.title}</span>
        <span className="agent-track-artist">{view.artist}</span>
      </span>
      <span className="agent-track-meta">
        <span className="agent-track-keyline">
          {view.key ? <CamelotKeyChip value={view.key} /> : null}
          {bpm ? (
            <span className="agent-track-bpm">
              {bpm}
              <span className="bpm-unit"> BPM</span>
            </span>
          ) : null}
        </span>
      </span>
      {onRemove ? (
        <button
          type="button"
          className="agent-user-track-card-remove"
          aria-label="Remove attached track"
          onClick={onRemove}
        >
          <IonIcon icon={closeOutline} />
        </button>
      ) : null}
    </div>
  );
};

export default AgentAttachedTrackCard;
